import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiError, placeOrderResponse, sellScreenPaymentInitialStateType } from "../types/sellScreenTypes";
import { InsertBulkProductIntoCartPayload, placeOrderPayload } from "../../components/sellScreen/sellScreenModule";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import { ZenToast } from "zen-core-elements";
import { getOneGiftCardResponse } from "../../components/payment/paymentModule";

const initialState: sellScreenPaymentInitialStateType = {
	placeOrderApi: { success: false, loader: false, ordersId: null },
	isRepaymentEnabled: false,
	paymentCompleted: false,
	totalAmount: 0,
	productCount: 0,
	paidAmount: 0,
	remainingAmount: 0,
	isSplitPayment: false,
	transactionData: []
};

// The placeorder API puts a JSON string in error.details (e.g. code DISCOUNT_INVALID with a displayMessage)
export const placeOrderErrorMessage = (details?: string) => {
	if (!details) return "Something went wrong, Please try again later"
	try {
		const parsed = JSON.parse(details)
		return parsed?.displayMessage || parsed?.message || details
	} catch {
		return details
	}
}

export const placeOrderApi = createAsyncThunk<
	placeOrderResponse,
	{ payload: placeOrderPayload; storeId: number },
	{ rejectValue: ApiError }
>(
	"newSellscreenPaymentSlice/placeOrder",
	async ({ payload, storeId }, { rejectWithValue }) => {
		try {
			let url = HttpRoutingService.replaceUrl(API.placeOrders, { storeId });
			return await HttpRoutingService.postMethod<placeOrderResponse>(url, payload, null, "v2/");
		} catch (err) {
			return rejectWithValue(err as ApiError);
		}
	}
);

export const redeemGiftCardApi = createAsyncThunk("newSellscreenPaymentSlice/redeemGiftCardApi", ({ storeId, coupon }: { storeId: number, coupon: string }) => {
	let url = HttpRoutingService.replaceUrl(API?.getOnePurchasedGiftCardForPayment, { storeId, coupon })
	return HttpRoutingService.getMethod<getOneGiftCardResponse>(url)
})

export const InsertBulkProductIntoCartApi = createAsyncThunk("newSellscreenPaymentSlice/InsertBulkProductIntoCartApi", ({ payload, storeId, customerId }: { payload: InsertBulkProductIntoCartPayload, storeId: number, customerId: number }) => {
	let url = HttpRoutingService.replaceUrl(API?.insertBulkProductIntoCart, { storeId, customerId })
	return HttpRoutingService?.postMethod<placeOrderResponse>(url, payload)
})

const newSellscreenPaymentSlice = createSlice({
	name: "newSellscreenPaymentSlice",
	initialState,
	reducers: {
		setTotalAmount: (state, action: PayloadAction<{ total: number, productCount: number }>) => {
			state.totalAmount = action.payload.total
			state.productCount = action.payload.productCount
			state.remainingAmount = action.payload.total
		},
		toggleSplitPayment: (state) => {
			state.isSplitPayment = !state.isSplitPayment
			state.transactionData = []
			state.paidAmount = 0
			state.remainingAmount = state.totalAmount
			state.isRepaymentEnabled = false
		},
		setTransactionData: (state, action: PayloadAction<{ data: sellScreenPaymentInitialStateType["transactionData"][0], id?: string }>) => {
			const transactionData = action.payload?.data
			console.log(transactionData, "transactionData");

			let oldPaidAmount = 0
			if (action?.payload?.id) {
				let data = state.transactionData?.map((item) => {
					if (item?._id == action?.payload?.id) {
						oldPaidAmount = item?._paidAmount
						return transactionData
					}
					else { return item }
				})
				state.transactionData = data
				state.paidAmount = (state.paidAmount - oldPaidAmount) + transactionData?._paidAmount
			} else {
				state.transactionData = [...state.transactionData, transactionData]
				state.paidAmount = state.paidAmount + transactionData?._paidAmount
			}
			state.remainingAmount = state.totalAmount - state.paidAmount

			if ((state.totalAmount <= state.paidAmount) && !state.isSplitPayment) {
				state.paymentCompleted = true
			}
			if (transactionData?._paymentMethod == "Cash") {
				state.isRepaymentEnabled = true
			}
		},
		removeTransactionData: (state, action: PayloadAction<string>) => {
			const id = action.payload
			let oldPaidAmount = 0
			let data = state.transactionData?.filter((item) => {
				if (item?._id == id) {
					oldPaidAmount = item?._paidAmount
					return false
				}
				else { return true }
			})
			state.transactionData = data
			state.paidAmount = state.paidAmount - oldPaidAmount
			state.remainingAmount = state.totalAmount - state.paidAmount
		},
		setSplitPaymentComplete: (state) => {
			state.paymentCompleted = true
			const hasCashPayment = state.transactionData?.some((item) => item?._paymentMethod === "Cash")
			state.isRepaymentEnabled = hasCashPayment
		},
		reFundAndRePay: (state) => {
			state.paymentCompleted = false
			// if (!state.isSplitPayment) {
			// 	state.isRepaymentEnabled = false
			// 	const findCashPayment = state.transactionData?.filter((item) => item?._paymentMethod !== "Cash")
			// 	state.transactionData = findCashPayment
			// 	state.paidAmount = findCashPayment?.reduce((acc, item) => acc + item?._paidAmount, 0)
			// 	state.remainingAmount = state.totalAmount - state.paidAmount
			// }
			if (!state.isSplitPayment) {
				state.isRepaymentEnabled = false
				state.transactionData = []
				state.paidAmount = 0
				state.remainingAmount = state.totalAmount
			}
		},
		onCompleteSale: () => {
			return { ...initialState }
		},
		checkForStoreCreditPayment: (state) => {
			const findStoreCreditPayment = state.transactionData?.filter((item) => item?._paymentMethod !== "Store Credit")
			state.transactionData = findStoreCreditPayment
			state.paidAmount = findStoreCreditPayment?.reduce((acc, item) => acc + item?._paidAmount, 0)
			state.remainingAmount = state.totalAmount - state.paidAmount
		}
	},
	extraReducers: (builder) => {
		builder.addCase(placeOrderApi.pending, (state) => {
			state.placeOrderApi.loader = true
		})
		builder.addCase(placeOrderApi.fulfilled, (state, action) => {
			state.placeOrderApi.loader = false
			if (action?.payload?.success) {
				action.payload.result.id
				state.placeOrderApi.success = true
				state.placeOrderApi.ordersId = action?.payload?.result?.id
				state.placeOrderApi.value = action?.payload?.result
				ZenToast({ type: "success", message: "Order placed successfully" })
			} else {
				state.placeOrderApi.success = false
				ZenToast({ type: "error", message: action?.payload?.message || "Something went wrong, Please try again later" })
			}
		})
		builder.addCase(placeOrderApi.rejected, (state, action) => {
			state.placeOrderApi = { loader: false, success: false, ordersId: null }
			ZenToast({ type: "error", message: placeOrderErrorMessage(action?.payload?.error?.details) })
		})
	}
});

export const {
	setTotalAmount,
	toggleSplitPayment,
	setTransactionData,
	onCompleteSale,
	reFundAndRePay,
	setSplitPaymentComplete,
	removeTransactionData,
	checkForStoreCreditPayment
} = newSellscreenPaymentSlice.actions;

export default newSellscreenPaymentSlice.reducer;